import React from "react";
import styled, { keyframes } from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AssignmentReturnIcon from "@material-ui/icons/AssignmentReturn";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import PaymentIcon from "@material-ui/icons/Payment";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ContactMailIcon from "@material-ui/icons/ContactMail";
import GetAppIcon from "@material-ui/icons/GetApp";
import GavelIcon from "@material-ui/icons/Gavel";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { CUSTOMER_CARE_NUMBER } from "../constants";
import { SUPPORT_WHATSAPP_DISPLAY } from "../config/market";
import { REFUND_TERMS } from "../config/customerPromise";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import BlockIcon from "@material-ui/icons/Block";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";

// ── Animations ──────────────────────────────────────────────────────────────
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Styled Components (matching Policy.jsx design system) ───────────────────
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 50%, #0f1a1c 100%);
  padding: var(--lusuku-nav-h, 120px) 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 80px;
    background: #f8f9fa;
    border-radius: 100% 100% 0 0;
    transform: translateY(50%);
  }

  @media (max-width: 768px) {
    padding: 220px 16px 120px;
    &::after { height: 50px; }
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(255, 193, 7, 0.15);
  border: 2px solid rgba(255, 193, 7, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  svg { font-size: 40px; color: #FFC107; }
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;
  @media (max-width: 768px) { font-size: 2rem; }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 600px;
  margin: 0 auto 24px;
  @media (max-width: 768px) { font-size: 1rem; }
`;

const LastUpdated = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 20px;
  border-radius: 50px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #FFC107 0%, #FF9800 100%);
  color: #1a1a1a;
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 24px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 193, 7, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 193, 7, 0.4);
  }
  svg { font-size: 20px; }
  @media (max-width: 768px) { width: 100%; justify-content: center; padding: 12px 24px; }
`;

const ContentSection = styled.section`
  padding: 60px 20px 80px;
  @media (max-width: 768px) { padding: 40px 16px 60px; }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const QuickNav = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  @media (max-width: 768px) { padding: 20px; gap: 8px; }
`;

const QuickNavTitle = styled.span`
  font-weight: 700;
  color: #324D54;
  margin-right: 8px;
  font-size: 0.95rem;
`;

const QuickNavLink = styled.a`
  color: #666;
  font-size: 0.85rem;
  text-decoration: none;
  padding: 6px 14px;
  border-radius: 20px;
  background: #f0f0f0;
  transition: all 0.2s ease;
  &:hover { background: #324D54; color: white; }
`;

const PolicyCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  animation: ${fadeInUp} 0.6s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.$delay || '0s'};
  @media (max-width: 768px) { padding: 24px; border-radius: 16px; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const SectionIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.$bg || 'linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)'};
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { font-size: 26px; color: white; }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  @media (max-width: 768px) { font-size: 1.25rem; }
`;

const SectionContent = styled.div`
  color: #555;
  font-size: 1rem;
  line-height: 1.8;
  p { margin-bottom: 16px; }
  strong { color: #1a1a1a; font-weight: 600; }
`;

const SubSection = styled.div`
  margin-top: 24px;
  padding-left: 20px;
  border-left: 3px solid #e0e0e0;
`;

const SubSectionTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 600;
  color: #324D54;
  margin-bottom: 12px;
`;

const DataList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0;
  display: grid;
  gap: 12px;
`;

const DataItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  background: #f8f9fa;
  border-radius: 10px;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;

  &::before {
    content: '${props => props.$icon || "\u2713"}';
    color: ${props => props.$iconColor || "#10B981"};
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 12px;
  padding: 20px 24px;
  margin: 20px 0;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  svg { color: #FFC107; font-size: 24px; flex-shrink: 0; margin-top: 2px; }
`;

const HighlightText = styled.p`
  color: #5a4a00;
  font-size: 0.95rem;
  line-height: 1.7;
  margin: 0;
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(37, 99, 235, 0.08) 100%);
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 12px;
  padding: 20px 24px;
  margin: 20px 0;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  svg { color: #3B82F6; font-size: 24px; flex-shrink: 0; margin-top: 2px; }
`;

const InfoText = styled.p`
  color: #1e3a5f;
  font-size: 0.95rem;
  line-height: 1.7;
  margin: 0;
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin: 20px 0;
`;

const StepCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  border-top: 3px solid ${props => props.$accent || '#324D54'};
`;

const StepNumber = styled.div`
  width: 36px;
  height: 36px;
  background: ${props => props.$bg || '#324D54'};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  margin: 0 auto 12px;
`;

const StepTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const StepDesc = styled.p`
  color: #666;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 24px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ContactCard = styled.div`
  background: linear-gradient(135deg, #324D54 0%, #1a2a2e 100%);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  color: white;
`;

const ContactIcon = styled.div`
  margin-bottom: 12px;
  svg { font-size: 2rem; color: #FFC107; }
`;

const ContactLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
`;

const ContactValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
`;

// ── Component ───────────────────────────────────────────────────────────────
const ReturnPolicy = () => {
    const handleDownloadPolicy = () => {
        const printWindow = window.open('', '_blank');

        const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Lusuku Return & Refund Policy</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #324D54;
          }
          .logo { font-size: 32px; font-weight: 800; color: #324D54; margin-bottom: 8px; }
          .logo span { color: #FFC107; }
          h1 { font-size: 28px; color: #324D54; margin-bottom: 8px; }
          .date { color: #666; font-size: 14px; }
          h2 {
            font-size: 18px;
            color: #324D54;
            margin: 30px 0 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #e0e0e0;
          }
          p { margin-bottom: 12px; text-align: justify; }
          ul { margin: 12px 0 12px 24px; }
          li { margin-bottom: 8px; }
          .highlight {
            background: #fff8e1;
            border-left: 4px solid #FFC107;
            padding: 12px 16px;
            margin: 16px 0;
            border-radius: 4px;
          }
          .contact-box {
            background: #f5f5f5;
            padding: 20px;
            border-radius: 8px;
            margin-top: 20px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            color: #666;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Lusuku<span>.</span></div>
          <h1>Return & Refund Policy</h1>
          <p class="date">Last Updated: ${REFUND_TERMS.policyUpdated}</p>
        </div>

        <h2>1. Shipping & Delivery</h2>
        <p><strong>Delivery Handling:</strong> Our primary mode of delivery is through our in-house Delivery Team, ensuring efficient and secure delivery of your orders. On occasions, we may also collaborate with trusted logistics companies to handle deliveries at no additional cost to you.</p>
        <p><strong>Payment Methods:</strong> We offer Mobile Money (MTN MoMo, Airtel Money), Credit Card, and Cash on Delivery. All online payments are covered by our Buyer Protection Program.</p>
        <p><strong>Delivery Times:</strong> Standard delivery is within 24 hours to 3 days, subject to your location. Detailed timelines are provided at checkout and on product detail pages.</p>

        <h2>2. Buyer Protection Program</h2>
        <p>All purchases made through Lusuku using our supported payment channels are covered by our Buyer Protection Program. Your payment is held securely until you confirm receipt and satisfaction with your order.</p>
        <div class="highlight">
          <strong>What's Covered:</strong> Wrong items, damaged goods, defective products, items not as described, and missing items from your order.
        </div>

        <h2>3. Return Window: ${REFUND_TERMS.reportWindow}</h2>
        <p>Our return policy is active for <strong>${REFUND_TERMS.reportWindow}</strong> from the time of delivery. During this period, you may request a return or exchange if the item does not meet your expectations.</p>
        <p><strong>Rejection at Delivery:</strong> If you're not satisfied with an item at the time of delivery, you may reject it immediately. Refunds for rejected items are sent within ${REFUND_TERMS.doorRejectionRefund} for prepaid orders.</p>

        <h2>4. Reasons for Return</h2>
        <ul>
          <li>Item is damaged or defective upon arrival</li>
          <li>Wrong item delivered (different from what was ordered)</li>
          <li>Item does not match the product description or images on the listing</li>
          <li>Item is missing parts, accessories, or components</li>
          <li>Quality does not meet reasonable expectations</li>
        </ul>

        <h2>5. Eligibility for Return</h2>
        <ul>
          <li>The item must be unused and in the same condition as received</li>
          <li>It must be in its original packaging with all tags, seals, and accessories intact</li>
          <li>The return must be requested within ${REFUND_TERMS.reportWindow} of delivery</li>
          <li>You must provide proof of purchase (order confirmation or receipt)</li>
          <li>For damaged or defective items, photo evidence must be provided</li>
        </ul>

        <h2>6. Non-Returnable Items</h2>
        <p>The following items cannot be returned for hygiene, safety, or perishability reasons:</p>
        <ul>
          <li>Perishable goods (food, fresh produce, beverages, dairy)</li>
          <li>Intimate or sanitary products (underwear, swimwear, lingerie)</li>
          <li>Personal care items (cosmetics, fragrances, skincare - if opened)</li>
          <li>Hazardous materials, flammable liquids or gases</li>
          <li>Newspapers, magazines, and periodicals</li>
          <li>Gift cards, vouchers, and downloadable products</li>
          <li>Items that have been used, altered, or damaged after delivery</li>
          <li>Pharmaceutical and health supplement products (once opened)</li>
        </ul>

        <h2>7. Order Cancellation</h2>
        <p><strong>Before dispatch:</strong> Orders can be cancelled free of charge before they are dispatched to a rider for delivery.</p>
        <p><strong>After dispatch but before delivery:</strong> If a rider has been assigned but has not yet picked up the item, cancellation may incur a small service fee.</p>
        <p><strong>After pickup:</strong> Once the rider has picked up your order, cancellation requests are handled on a case-by-case basis. If cancellation is approved, a return trip fee may apply.</p>

        <h2>8. Reporting Issues</h2>
        <p>If you receive a wrong, damaged, or missing item, report it immediately through any of our support channels. Providing photo or video evidence of the issue will help us process your claim faster.</p>

        <h2>9. How to Initiate a Return</h2>
        <p>Step 1: Contact our customer service via email, phone, or WhatsApp within ${REFUND_TERMS.reportWindow} of delivery.</p>
        <p>Step 2: Provide your order number, reason for return, and photo evidence (for damaged or defective items).</p>
        <p>Step 3: Our team replies within ${REFUND_TERMS.firstReply} with next steps.</p>
        <p>Step 4: Return the item as instructed or arrange a pickup with our team.</p>
        <p>Step 5: Once received and inspected, your refund is processed.</p>

        <h2>10. Refund Process</h2>
        <p>Once your return is received and inspected, we will notify you of the approval or rejection of your refund.</p>
        <ul>
          <li><strong>Rejected-at-delivery refunds:</strong> Sent within ${REFUND_TERMS.doorRejectionRefund} for prepaid orders</li>
          <li><strong>Mobile Money refunds:</strong> Sent to the number used for payment within ${REFUND_TERMS.mobileMoneyRefund} of approval</li>
          <li><strong>Card refunds:</strong> Sent within ${REFUND_TERMS.cardRefundSent} of approval; your bank may take ${REFUND_TERMS.cardBankTime} to show it</li>
          <li><strong>Cash on Delivery refunds:</strong> Sent via Mobile Money to a number you provide, within ${REFUND_TERMS.mobileMoneyRefund} of approval</li>
          <li><strong>Partial refunds:</strong> May apply if items are not in original condition, have missing parts, or were reported after the ${REFUND_TERMS.reportWindow} window</li>
        </ul>

        <h2>11. Contact Us</h2>
        <p>For any questions about this Return & Refund Policy:</p>
        <div class="contact-box">
          <p><strong>Email:</strong> support@lusuku.ug</p>
          <p><strong>Phone:</strong> ${CUSTOMER_CARE_NUMBER}</p>
          <p><strong>WhatsApp:</strong> ${SUPPORT_WHATSAPP_DISPLAY}</p>
          <p style="margin-top: 12px;"><strong>Lusuku Fresh Services</strong><br>SN Plaza, Ntinda Kigoowa, Tuund Road<br>Kampala, Uganda</p>
        </div>

        <div class="footer">
          <p>&copy; ${new Date().getFullYear()} Lusuku Fresh Services. All rights reserved.</p>
          <p style="margin-top: 8px;">This document was generated from lusuku.ug/return-policy</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `;

        printWindow.document.write(pdfContent);
        printWindow.document.close();
    };

    return (
        <PageWrapper>
            <Navbar />

            {/* Hero Section */}
            <HeroSection>
                <HeroContent>
                    <HeroIcon>
                        <AssignmentReturnIcon />
                    </HeroIcon>
                    <HeroTitle>Return & Refund Policy</HeroTitle>
                    <HeroSubtitle>
                        Your satisfaction is our priority. Learn about our shipping, return, and
                        refund processes to shop with confidence on Lusuku.
                    </HeroSubtitle>
                    <LastUpdated>Last Updated: {REFUND_TERMS.policyUpdated}</LastUpdated>
                    <DownloadButton onClick={handleDownloadPolicy}>
                        <GetAppIcon />
                        Download Policy
                    </DownloadButton>
                </HeroContent>
            </HeroSection>

            {/* Content */}
            <ContentSection>
                <Container>
                    {/* Quick Navigation */}
                    <QuickNav>
                        <QuickNavTitle>Jump to:</QuickNavTitle>
                        <QuickNavLink href="#shipping">Shipping</QuickNavLink>
                        <QuickNavLink href="#buyer-protection">Buyer Protection</QuickNavLink>
                        <QuickNavLink href="#return-window">Return Window</QuickNavLink>
                        <QuickNavLink href="#reasons">Reasons for Return</QuickNavLink>
                        <QuickNavLink href="#eligibility">Eligibility</QuickNavLink>
                        <QuickNavLink href="#non-returnable">Non-Returnable</QuickNavLink>
                        <QuickNavLink href="#cancellation">Cancellation</QuickNavLink>
                        <QuickNavLink href="#how-to-return">How to Return</QuickNavLink>
                        <QuickNavLink href="#refunds">Refunds</QuickNavLink>
                        <QuickNavLink href="#contact">Contact</QuickNavLink>
                    </QuickNav>

                    {/* ── 1. Shipping & Delivery ───────────────────────────────────── */}
                    <PolicyCard id="shipping" $delay="0.1s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)">
                                <LocalShippingIcon />
                            </SectionIcon>
                            <SectionTitle>Shipping & Delivery</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                <strong>Delivery Handling:</strong> Our primary mode of delivery is through our
                                in-house Delivery Team, ensuring efficient and secure delivery of your orders.
                                On occasions, we may also collaborate with trusted logistics companies to handle
                                deliveries at no additional cost to you as our valued customer.
                            </p>
                            <p>
                                <strong>Payment Methods:</strong> We offer a range of payment options for your
                                convenience, including Mobile Money (MTN MoMo, Airtel Money), Credit Card, and
                                Cash on Delivery. We encourage you to use these options to benefit from our
                                Buyer Protection Program.
                            </p>
                            <p>
                                <strong>Delivery Times:</strong> We aim for a standard delivery time of within
                                24 hours to 3 days, subject to your location. Detailed delivery
                                timelines for each product are provided at the checkout stage and on their
                                respective detail pages.
                            </p>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 2. Buyer Protection ──────────────────────────────────────── */}
                    <PolicyCard id="buyer-protection" $delay="0.15s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #10B981 0%, #059669 100%)">
                                <VerifiedUserIcon />
                            </SectionIcon>
                            <SectionTitle>Buyer Protection Program</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                All purchases made through Lusuku using our supported payment channels are
                                covered by our <strong>Buyer Protection Program</strong>. Your payment is held
                                securely until you confirm receipt and satisfaction with your order.
                            </p>
                            <HighlightBox>
                                <VerifiedUserIcon />
                                <HighlightText>
                                    <strong>What's Covered:</strong> Wrong items received, damaged goods,
                                    defective products, items that don't match the listing description or images,
                                    and missing items from your order. Our protection ensures prompt resolution
                                    and reimbursement.
                                </HighlightText>
                            </HighlightBox>
                            <DataList>
                                <DataItem>Payment is secured until delivery is confirmed</DataItem>
                                <DataItem>Immediate refund if you reject an item at the point of delivery</DataItem>
                                <DataItem>{REFUND_TERMS.reportWindow} post-delivery coverage for quality and accuracy issues</DataItem>
                                <DataItem>Applies to all supported payment methods (Mobile Money, Card, COD)</DataItem>
                            </DataList>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 3. Return Window ─────────────────────────────────────────── */}
                    <PolicyCard id="return-window" $delay="0.2s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)">
                                <ScheduleIcon />
                            </SectionIcon>
                            <SectionTitle>Return Window: {REFUND_TERMS.reportWindow}</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                Our return policy is active for <strong>{REFUND_TERMS.reportWindow}</strong> from the time you
                                receive your item. During this period, you may request a return or exchange
                                if the item does not meet your expectations.
                            </p>
                            <SubSection>
                                <SubSectionTitle>Rejection at Delivery</SubSectionTitle>
                                <p>
                                    If you are not satisfied with an item at the time of delivery, you may
                                    reject it immediately. Our delivery rider will return the item and your
                                    refund will be sent within <strong>{REFUND_TERMS.doorRejectionRefund}</strong> for prepaid orders.
                                    No return request is needed in this case.
                                </p>
                            </SubSection>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 4. Reasons for Return ────────────────────────────────────── */}
                    <PolicyCard id="reasons" $delay="0.25s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)">
                                <ReportProblemIcon />
                            </SectionIcon>
                            <SectionTitle>Accepted Reasons for Return</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>You may initiate a return for any of the following reasons:</p>
                            <DataList>
                                <DataItem>Item is damaged or defective upon arrival</DataItem>
                                <DataItem>Wrong item delivered (different from what was ordered)</DataItem>
                                <DataItem>Item does not match the product description or images on the listing</DataItem>
                                <DataItem>Item is missing parts, accessories, or components</DataItem>
                                <DataItem>Quality does not meet reasonable expectations</DataItem>
                            </DataList>
                            <InfoBox>
                                <ReportProblemIcon />
                                <InfoText>
                                    <strong>Tip:</strong> Take photos or a short video when you open your package.
                                    This serves as evidence if you need to report an issue and helps us process
                                    your claim faster.
                                </InfoText>
                            </InfoBox>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 5. Eligibility ───────────────────────────────────────────── */}
                    <PolicyCard id="eligibility" $delay="0.3s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)">
                                <CheckCircleIcon />
                            </SectionIcon>
                            <SectionTitle>Eligibility for Return</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>To qualify for a return, items must meet all of the following conditions:</p>
                            <DataList>
                                <DataItem>The item must be unused and in the same condition as when you received it</DataItem>
                                <DataItem>It must be in its original packaging with all tags, seals, and accessories intact</DataItem>
                                <DataItem>The return request must be made within {REFUND_TERMS.reportWindow} of delivery</DataItem>
                                <DataItem>You must provide proof of purchase (order confirmation or receipt)</DataItem>
                                <DataItem>For damaged or defective items, photo or video evidence must be submitted</DataItem>
                            </DataList>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 6. Non-Returnable Items ──────────────────────────────────── */}
                    <PolicyCard id="non-returnable" $delay="0.35s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #EF4444 0%, #DC2626 100%)">
                                <BlockIcon />
                            </SectionIcon>
                            <SectionTitle>Non-Returnable Items</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                The following categories of goods cannot be returned for hygiene, safety,
                                or perishability reasons:
                            </p>
                            <DataList>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Perishable goods (food, fresh produce, beverages, dairy products)</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Intimate or sanitary products (underwear, swimwear, lingerie)</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Personal care items such as cosmetics, fragrances, and skincare (if opened)</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Hazardous materials, flammable liquids or gases</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Newspapers, magazines, and periodicals</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Gift cards, vouchers, and downloadable products</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Items that have been used, altered, or damaged after delivery</DataItem>
                                <DataItem $icon={"\u2715"} $iconColor="#EF4444">Pharmaceutical and health supplement products (once opened)</DataItem>
                            </DataList>
                            <HighlightBox>
                                <GavelIcon />
                                <HighlightText>
                                    <strong>Exception:</strong> Even for non-returnable categories, if you receive a
                                    wrong, damaged, or defective item, you are still eligible for a return or refund
                                    under our Buyer Protection Program.
                                </HighlightText>
                            </HighlightBox>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 7. Order Cancellation ────────────────────────────────────── */}
                    <PolicyCard id="cancellation" $delay="0.4s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)">
                                <CancelIcon />
                            </SectionIcon>
                            <SectionTitle>Order Cancellation</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                You can cancel your order at different stages. The applicable charges depend
                                on the stage of the delivery process:
                            </p>
                            <SubSection>
                                <SubSectionTitle>Before Dispatch</SubSectionTitle>
                                <p>
                                    Orders can be cancelled <strong>free of charge</strong> before they are
                                    dispatched to a rider. Contact our team or cancel directly from your order
                                    history.
                                </p>
                            </SubSection>
                            <SubSection>
                                <SubSectionTitle>After Rider Assigned, Before Pickup</SubSectionTitle>
                                <p>
                                    If a rider has been assigned but has not yet picked up your item, cancellation
                                    may incur a <strong>small service fee</strong> to compensate for the rider's time.
                                </p>
                            </SubSection>
                            <SubSection>
                                <SubSectionTitle>After Pickup</SubSectionTitle>
                                <p>
                                    Once the rider has picked up your order, cancellation requests are handled on
                                    a case-by-case basis. If cancellation is approved, a return trip fee may apply.
                                    The product cost will be refunded once the item is returned to the vendor.
                                </p>
                            </SubSection>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 8. How to Initiate a Return ───────────────────────────────── */}
                    <PolicyCard id="how-to-return" $delay="0.45s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #EC4899 0%, #DB2777 100%)">
                                <AssignmentReturnIcon />
                            </SectionIcon>
                            <SectionTitle>How to Initiate a Return</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                Follow these steps to request a return within <strong>{REFUND_TERMS.reportWindow}</strong> of delivery:
                            </p>
                            <StepsGrid>
                                <StepCard $accent="#6366F1">
                                    <StepNumber $bg="#6366F1">1</StepNumber>
                                    <StepTitle>Contact Us</StepTitle>
                                    <StepDesc>
                                        Reach out via email, phone, or WhatsApp with your order number.
                                    </StepDesc>
                                </StepCard>
                                <StepCard $accent="#3B82F6">
                                    <StepNumber $bg="#3B82F6">2</StepNumber>
                                    <StepTitle>Provide Details</StepTitle>
                                    <StepDesc>
                                        Share the reason for return and attach photo/video evidence if applicable.
                                    </StepDesc>
                                </StepCard>
                                <StepCard $accent="#10B981">
                                    <StepNumber $bg="#10B981">3</StepNumber>
                                    <StepTitle>Review & Instructions</StepTitle>
                                    <StepDesc>
                                        Our team replies within {REFUND_TERMS.firstReply} with next steps.
                                    </StepDesc>
                                </StepCard>
                                <StepCard $accent="#F59E0B">
                                    <StepNumber $bg="#F59E0B">4</StepNumber>
                                    <StepTitle>Return the Item</StepTitle>
                                    <StepDesc>
                                        Ship the item back or arrange a pickup with our delivery team.
                                    </StepDesc>
                                </StepCard>
                                <StepCard $accent="#8B5CF6">
                                    <StepNumber $bg="#8B5CF6">5</StepNumber>
                                    <StepTitle>Refund Processed</StepTitle>
                                    <StepDesc>
                                        Once received and inspected, your refund is processed to your original payment method.
                                    </StepDesc>
                                </StepCard>
                            </StepsGrid>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 9. Refund Process ─────────────────────────────────────────── */}
                    <PolicyCard id="refunds" $delay="0.5s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)">
                                <PaymentIcon />
                            </SectionIcon>
                            <SectionTitle>Refund Process</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                Once your return is received and inspected, we will notify you of the
                                approval or rejection of your refund.
                            </p>
                            <DataList>
                                <DataItem>
                                    <strong>Rejected-at-delivery refunds</strong> are sent within {REFUND_TERMS.doorRejectionRefund}
                                    for prepaid orders
                                </DataItem>
                                <DataItem>
                                    <strong>Mobile Money refunds</strong> are sent to the number used for
                                    payment within {REFUND_TERMS.mobileMoneyRefund} of approval
                                </DataItem>
                                <DataItem>
                                    <strong>Card refunds</strong> are sent within {REFUND_TERMS.cardRefundSent} of approval; your bank
                                    may take {REFUND_TERMS.cardBankTime} to show it
                                </DataItem>
                                <DataItem>
                                    <strong>Cash on Delivery refunds</strong> are sent via Mobile Money to a number
                                    you provide, within {REFUND_TERMS.mobileMoneyRefund} of approval
                                </DataItem>
                            </DataList>
                            <HighlightBox>
                                <GavelIcon />
                                <HighlightText>
                                    <strong>Partial Refunds:</strong> In certain situations, only partial refunds
                                    may be granted, for example if items are not in original condition,
                                    have missing parts, or were reported after the {REFUND_TERMS.reportWindow}
                                    window with an accepted exception.
                                </HighlightText>
                            </HighlightBox>
                        </SectionContent>
                    </PolicyCard>

                    {/* ── 10. Contact ──────────────────────────────────────────────── */}
                    <PolicyCard id="contact" $delay="0.55s">
                        <SectionHeader>
                            <SectionIcon $bg="linear-gradient(135deg, #F43F5E 0%, #E11D48 100%)">
                                <ContactMailIcon />
                            </SectionIcon>
                            <SectionTitle>Contact Us</SectionTitle>
                        </SectionHeader>
                        <SectionContent>
                            <p>
                                If you have any questions about our Return & Refund Policy, our customer
                                service team is here to help:
                            </p>
                            <ContactGrid>
                                <ContactCard>
                                    <ContactIcon><EmailIcon /></ContactIcon>
                                    <ContactLabel>Email</ContactLabel>
                                    <ContactValue>support@lusuku.ug</ContactValue>
                                </ContactCard>
                                <ContactCard>
                                    <ContactIcon><PhoneIcon /></ContactIcon>
                                    <ContactLabel>Phone</ContactLabel>
                                    <ContactValue>{CUSTOMER_CARE_NUMBER}</ContactValue>
                                </ContactCard>
                                <ContactCard>
                                    <ContactIcon><WhatsAppIcon /></ContactIcon>
                                    <ContactLabel>WhatsApp</ContactLabel>
                                    <ContactValue>{SUPPORT_WHATSAPP_DISPLAY}</ContactValue>
                                </ContactCard>
                            </ContactGrid>
                        </SectionContent>
                    </PolicyCard>
                </Container>
            </ContentSection>

            <Footer />
        </PageWrapper>
    );
};

export default ReturnPolicy;
